<template>
    <div>
        <div class="w-100 d-flex justify-content-between">
            <v-alert type="info" text class="mb-5" v-if="provider === undefined && !hideTopInfo">
                Le paramétrage du formulaire ne pourra plus être modifié après le premier envoi de la campagne et
                s'appliquera aussi à ses relances.
            </v-alert>
            <div class="m-2">
                <v-btn outlined text color="primary" class="text-none" @click="openModal = true">
                    <v-icon class="mr-2">remove_red_eye</v-icon>
                    Prévisualiser
                </v-btn>
            </div>
        </div>
        <div v-if="hideNextButton === undefined" class="mb-10">
            <v-expansion-panels>
                <v-expansion-panel style="background-color: #f5f5f5;">
                    <v-expansion-panel-header>
                        <div class="table-title">
                            Utiliser un modèle de formulaires
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-alert text class="mb-5" type="info" border="right" colored-border>
                            Pour réutiliser une liste prédéfinie de champs sur plusieurs campagnes, vous pouvez
                            créer un modèle de formulaire
                            <router-link to="/configuration/form-template">ici
                            </router-link>
                            .<br>
                            <span v-if="templates.length">Vous pourrez ensuite le sélectionner dans la liste ci-dessous
                                pour précharger les champs</span>
                        </v-alert>

                        <div class="d-flex mb-5" v-if="templates.length && !loading">
                            <div class="mt-auto mb-auto mr-3">
                                <v-label>Charger un modèle de formulaire</v-label>
                            </div>

                            <v-select hide-details outlined :items="templates" @input="loadTemplate" item-text="name"
                                      return-object style="padding-top: 0!important;">
                            </v-select>
                        </div>
                        <div class="d-flex mb-5" v-else-if="loading">
                            <v-progress-circular class="progressbar mr-2" indeterminate size="40" width="4">
                            </v-progress-circular>
                        </div>

                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>

        <div class="mb-10">
            <div class="d-flex justify-content-between my-5">
                <h2 style="color: #0b697e">Texte d'introduction</h2>
                <v-btn outlined text color="primary" class="text-none" @click="loadIntroModal = true">
                    <v-icon class="mr-2">content_copy</v-icon>
                    Charger un texte d'introduction configuré
                </v-btn>
            </div>
            <ckeditor
              :editor="editor"
              :config="editorConfig"
              :value="txtIntro"
              @input="updateTxtIntro"
            ></ckeditor>
        </div>

        <h2 class="mb-5 mt-5" style="color: #0b697e">Champs du formulaire</h2>

        <v-alert type="info" text class="mb-5" v-if="provider === undefined && !hideTopInfo && aspect">
            Les champs marqués par un symbole {{aspectElement.icon}} seront transmis dans <span class="font-weight-bold">l'aspect
            {{aspectElement.label}}</span> sur Apidae. L'absence de ce symbole indique que le champ sera transmis dans l'aspect
            Standard.
        </v-alert>

        <div class="tabulation-title d-flex justify-content-between">
            <h3 v-if="nonTypeDepenant.length">
                Champs communs
            </h3>
        </div>

        <div class="d-flex justify-content-between flex-wrap gap-20 p-1 mb-10">
            <v-card v-for="tabulation in nonTypeDepenant" :key="tabulation.id" class="info-card">
                <v-app-bar dark class="bg-primary">
                    <div class="d-flex justify-content-between w-100">
                        <div class="d-flex flex-row">
                            <v-checkbox class="mt-auto mb-auto" style="padding-top: 0!important;"
                                        v-model="checkedTabulations[tabulation.id]"
                                        @change="checkTabulation($event, tabulation)"
                                        hide-details>
                            </v-checkbox>

                            <v-toolbar-title class="mt-auto mb-auto" style="padding-top: 0!important;">
                                {{ tabulation.label }}
                            </v-toolbar-title>
                        </div>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on"
                                       :color="(tabulation.new_title !== undefined && tabulation.new_title !== null
                                   && tabulation.new_title !== '') || (tabulation.help_description !== undefined &&
                                    tabulation.help_description !== null && tabulation.help_description !== '') ?
                                    'warning' : 'gray'" icon @click="editTabulation(tabulation)">
                                    <v-icon>edit</v-icon>
                                </v-btn>
                            </template>
                            <span>Configurer la section</span>
                        </v-tooltip>
                    </div>
                </v-app-bar>

                <v-card-text>
                    <v-treeview selectable selected-color="primary" item-key="_id" rounded
                                :value="selectedFields[tabulation.id]"
                                @input="updateSelectedFields(tabulation.id, $event, selectedFields[tabulation.id])"
                                return-object :open-on-click="true" :items="tabulation.children">

                        <template v-slot:label="{ item }">
                            <div class="d-flex justify-content-start">
                                <div v-if="item.new_title !== undefined && item.new_title !== null && item.new_title !== ''">
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{ item.new_title }}</span>
                                        </template>
                                        <span>
                                            {{ item.new_title }}
                                            <span v-if="isAspectField(item)" class="ml-2">{{aspectElement.icon}}</span>
                                        </span>
                                    </v-tooltip>
                                </div>
                                <div v-else>
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{ item.name }}</span>
                                        </template>
                                        <span>
                                            {{ item.name }}
                                            <span v-if="isAspectField(item)" class="ml-2">{{aspectElement.icon}}</span>
                                        </span>
                                    </v-tooltip>
                                </div>

                                <div v-if="isAspectField(item)" class="ml-2">
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{aspectElement.icon}}</span>
                                        </template>
                                        <span>Ce champ concerne l'aspect</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </template>

                        <template v-slot:append="{ item }">
                            <v-tooltip slot="append-outer" bottom
                                       v-if="(item.children === undefined || !item.children.length) && hasFieldEditionEnabled(item)">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" :color="(item.new_title !== null && item.new_title !== '') ||
                                                                                       (item.help_description !== null && item.help_description !== '') ?
                                    'warning' : 'gray'" icon @click="editField(item)">
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                </template>
                                <span>Configurer le champ</span>
                            </v-tooltip>

                            <v-tooltip slot="append-outer" bottom
                                       v-if="(item.children === undefined || !item.children.length) && hasFieldRequiredEnabled(item)">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" color="red" icon @click="requiredField(item, tabulation)">
                                        <v-icon v-if="item.required">star</v-icon>
                                        <v-icon v-else>star_border</v-icon>
                                    </v-btn>
                                </template>
                                <span v-if="item.code === 'IMAGE'"> Rendre le copyright de l'image obligatoire</span>
                                <span v-else>Rendre le champ obligatoire</span>
                            </v-tooltip>

                            <v-btn v-else icon disabled>
                            </v-btn>

                        </template>
                    </v-treeview>
                </v-card-text>
            </v-card>
        </div>

        <div class="tabulation-title d-flex justify-content-between">
            <h3 v-if="typeDependant.length">
                Champs dépendants des catégories
            </h3>
        </div>

        <div class="d-flex justify-content-between flex-wrap p-1 gap-20">
            <v-card v-for="tabulation in typeDependant"
                    :key="tabulation.id"
                    v-if="isTabulationEnabled(tabulation)"
                    class="info-card">
                <v-app-bar dark class="bg-primary">
                    <v-checkbox class="mt-auto mb-auto" style="padding-top: 0!important;"
                                v-model="checkedTabulations[tabulation.id]"
                                @change="checkTabulation($event, tabulation)"
                                hide-details>
                    </v-checkbox>

                    <v-toolbar-title>{{ tabulation.label }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                </v-app-bar>

                <v-card-text>
                    <v-treeview selectable selected-color="primary" item-key="_id" rounded
                                :value="selectedFields[tabulation.id]"
                                @input="updateSelectedFields(tabulation.id, $event)"
                                return-object :open-on-click="true" :items="tabulation.children">

                        <template v-slot:label="{ item }">
                            <div class="d-flex justify-content-start">
                                <div v-if="item.new_title !== undefined && item.new_title !== null && item.new_title !== ''">
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{ item.new_title }}</span>
                                        </template>
                                        <span>
                                            {{ item.new_title }}
                                            <span v-if="isAspectField(item)" class="ml-2">{{aspectElement.icon}}</span>
                                        </span>
                                    </v-tooltip>
                                </div>
                                <div v-else>
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{ item.name }}</span>
                                        </template>
                                        <span>
                                            {{ item.name }}
                                            <span v-if="isAspectField(item)" class="ml-2">{{aspectElement.icon}}</span>
                                        </span>
                                    </v-tooltip>
                                </div>

                                <div v-if="isAspectField(item)" class="ml-2">
                                    <v-tooltip slot="append-outer" bottom open-delay="700">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{aspectElement.icon}}</span>
                                        </template>
                                        <span>Ce champ concerne l'aspect</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </template>

                        <template v-slot:append="{ item }">
                            <v-tooltip slot="append-outer" bottom
                                       v-if="(item.children === undefined || !item.children.length) && hasFieldEditionEnabled(item)">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" :color="(item.new_title !== null && item.new_title !== '') ||
                                                                                       (item.help_description !== null && item.help_description !== '') ?
                                    'warning' : 'gray'" icon @click="editField(item)">
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                </template>
                                <span>Configurer le champ</span>
                            </v-tooltip>

                            <v-tooltip slot="append-outer" bottom
                                       v-if="(item.children === undefined || !item.children.length) && item.name === 'Portée'">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" color="red" icon>
                                        <v-icon v-if="item.required">star</v-icon>
                                        <v-icon v-else>star_border</v-icon>
                                    </v-btn>
                                </template>
                                <span>Le champs portée des fêtes et manifestation est toujours obligatoire</span>
                            </v-tooltip>

                            <v-tooltip slot="append-outer" bottom
                                       v-else-if="(item.children === undefined || !item.children.length)">
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" color="red" icon @click="requiredField(item, tabulation)">
                                        <v-icon v-if="item.required">star</v-icon>
                                        <v-icon v-else>star_border</v-icon>
                                    </v-btn>
                                </template>
                                <span>Rendre le champ obligatoire</span>
                            </v-tooltip>

                            <v-btn v-else icon disabled>
                            </v-btn>

                        </template>
                    </v-treeview>
                </v-card-text>
            </v-card>
        </div>

        <div class="text-right mb-5 mt-5" v-if="hideNextButton === undefined || !hideNextButton">
            <v-btn class="default-button" @click="setCurrentStep(parseInt(currentStep) + 1)">Passer à l'étape
                suivante
            </v-btn>
        </div>

        <v-dialog ref="dialog" v-model="openModal" persistent width="450px">
            <v-card>
                <v-card-title>
                    <span class="headline">Prévisualiser un formulaire</span>
                </v-card-title>
                <v-card-text>
                    <v-select :items="availableCategories" outlined class="no-details mb-5" item-text="label"
                              v-model="selectedCategory" return-object label="Catégorie">
                    </v-select>

                    <div class="d-flex justify-content-end gap-10">
                        <v-btn @click="closeModal">Fermer</v-btn>
                        <v-btn color="primary" :disabled="selectedCategory == null" @click="showPreview">Voir</v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="fieldModal" width="600px" persistent>
            <v-card v-if="currentField !== null">
                <v-card-title>
                    <span class="headline">Editer : {{ currentField.name }}</span>
                </v-card-title>

                <v-card-text>
                    <v-text-field outlined label="Titre du champ" :value="newTitle" :placeholder="currentField.name"
                                  @input="updateTitle">
                    </v-text-field>
                    <v-text-field outlined label="Aide à la saisie" :value="helpDescription"
                                  @input="updateHelpDescription"></v-text-field>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="fieldModal = false">Fermer</v-btn>
                    <v-btn class="default-button" @click="saveField()">Enregistrer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="tabulationModal" width="600px" persistent>
            <v-card v-if="currentTabulation !== null">
                <v-card-title>
                    <span class="headline">Editer : {{ currentTabulation.label }}</span>
                </v-card-title>

                <v-card-text>
                    <v-text-field outlined label="Titre de la section" :value="newTabTitle"
                                  :placeholder="currentTabulation.label"
                                  @input="updateTabTitle">
                    </v-text-field>
                    <v-text-field outlined label="Aide à la saisie" :value="tabDesc"
                                  @input="updateTabDescription"></v-text-field>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="tabulationModal = false">Fermer</v-btn>
                    <v-btn class="default-button" @click="saveTabulation()">Enregistrer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="loadIntroModal" width="900px" persistent>
            <v-card>
                <v-card-title class="d-flex justify-content-between">
                    <span class="headline">Charger un texte d'introduction configuré</span>
                    <div>
                        <v-btn color="error" @click="loadIntroModal = false" class="mr-3">Fermer</v-btn>
                        <v-btn class="default-button" :disabled="selectedIntroType === null" @click="loadTextIntro">Charger</v-btn>
                    </div>
                </v-card-title>

                <v-card-text>
                    <v-select :items="introTypes" outlined class="no-details mb-5" item-text="label"
                              v-model="selectedIntroType" return-object label="Type de texte d'introduction">
                    </v-select>

                    <ckeditor
                      :editor="editor"
                      :value="selectedIntroType === 'Mise à jour Standard' ? txtIntroCmaj : txtIntroAspect"
                      disabled
                      v-if="selectedIntroType !== null"
                    ></ckeditor>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapMutations, mapState } from "vuex"
import _ from "lodash"
import Loading from "@/components/tools/Loading.vue"
import ClassicEditor from "../../../../plugins/ckeditor"
import { aspectElements, aspectFields } from "../../../../static/webform"

export default {
    name: "Information",
    components: { Loading },
    props: ["tabulations", "selectAll", "hideNextButton", "provider", "hideTopInfo", 'updateMode', "aspect"],
    data() {
        return {
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    shouldNotGroupWhenFull: true,
                    items: [
                        'heading',
                        '|',
                        'alignment',
                        'bold',
                        'italic',
                        'underline',
                        'fontSize',
                        'fontColor',
                        'fontBackgroundColor',
                        'link',
                        'bulletedList',
                        'numberedList',
                        'imageUpload',
                        'blockQuote',
                        'undo',
                        'redo'
                    ]
                },
            },
            typeDependant: [],
            nonTypeDepenant: [],
            checkedTabulations: {},
            openModal: false,
            selectedCategory: null,
            currentField: null,
            currentTabulation: null,
            fieldModal: false,
            tabulationModal: false,
            newTitle: null,
            helpDescription: null,
            newTabTitle: null,
            tabDesc: null,
            disabledEditField: ["GOOGLE", "FACEBOOK", "TWITTER", "TRIPADVISOR", "YELP", "YOUTUBE", "INSTAGRAM",
                "COMPLEMENT_OUVERTURE", "ALWAYS_OPEN", "OPEN_24", "NAME",
                "ROOM_DESCRIPTION_COURT", "ROOM_DESCRIPTION", "MAX_CAPACITY_PERS", "AREA", "ROOM_HEIGHT",
                "NATURAL_LIGHT", "ROOM_U", "ROOM_BANQUET", "ROOM_SQUARE", "ROOM_CLASS", "ROOM_COCKTAIL", "ROOM_THEATRE",
                "Capacites", "CONTACT_TITLE", "CONTACT_EMAIL", "CONTACT_PHONE", "CONTACT_FIRST_NAME", "CONTACT_LAST_NAME",
                "ContactFonction", "LINKEDIN", "TIKTOK",
            ],
            enabledRequiredField: ["OFFER_NAME", "DESC_COURT_DE", "DESC_COURT_EN", "DESC_COURT_ES", "DESC_COURT_FR", "DESC_COURT_IT", "DESC_COURT_JA", "DESC_COURT_NL",
                "DESC_COURT_RU", "DESC_COURT_ZH", "DESC_LONG_DE", "DESC_LONG_EN", "DESC_LONG_ES", "DESC_LONG_FR", "DESC_LONG_IT", "DESC_LONG_JA", "DESC_LONG_NL",
                "DESC_LONG_RU", "DESC_LONG_ZH", "ModePaiement", "Langue", "DemarcheEcoResponsable", "TypologiePromoSitra", "DESC_TOURISME_DURABLE", "DESC_PORTRAIT",
                "DESC_2023_INNOVATION", "DESC_2024_INNOVATION", "DESC_2025_INNOVATION", "OFFER_PROMOTIONS", "OPENING_HOURS", "IMAGE", "DESC_HAND_AUDITIF", "DESC_HAND_MENTAL", "DESC_HAND_MOTEUR",
                "DESC_HAND_VISUEL", "DISABLED_RECEPTION", "NB_WHEELCHAIR_SIMULTANEOUSLY_AVAILABLE", "EMAIL", "PHONE", "ADDRESS", "ADDRESS3", "COMPLEMENT_ADDRESS", "CITY",
                "DESC_COVID19", "DispositionsCovid",
                "LabelTourismeHandicap", "AccessibilityCriteriaForPeopleWithReducedMobility", "AccessibilityCriteriaForHearingLoss", "AccessibilityCriteriaForMentalImpairment",
                "AccessibilityCriteriaForVisualImpairment",
            ],
            disabledRequiredSpecificFields: [
                "HebergementCollectifChaineEtLabel", "HebergementCollectifLabel", "DATE_CLASSEMENT_HLO", "HebergementLocatifTypeLabel",
                "HebergementLocatifLabel", "HotellerieClassement", "HotellerieLabel", "HotellerieChaine", "HotelleriePleinAirClassement",
                "HotelleriePleinAirChaine", "HotelleriePleinAirLabel", "StructureHabilitationPrestataire", "PatrimoineNaturelClassement",
                "DegustationLabelCharteQualite", "AopAocIgp", "RestaurationClassementGuide", "RestaurationChaine",
            ],
            templates: [],
            tarifIndication: {},
            lockedCheck: false,
            loading: false,
            loadIntroModal: false,
            selectedIntroType: null,
            introTypes: ['Mise à jour Standard'],
        }
    },
    methods: {
        ...mapMutations("createCmaj", ["setSelectedFields", "setCurrentStep", "addEditedTabulation",
            "deleteEditedTabulation", "resetEditedTabulations", "setTxtIntro"]),
        ...mapMutations("preview", ["setCategoryFields", "setPreviewEditedTabulations", "setPreviewTxtIntro"]),
        updateTitle: _.debounce(function(value) {
            this.newTitle = value
        }, 200),
        updateHelpDescription: _.debounce(function(value) {
            this.helpDescription = value
        }, 200),
        updateTabTitle: _.debounce(function(value) {
            this.newTabTitle = value
        }, 200),
        updateTabDescription: _.debounce(function(value) {
            this.tabDesc = value
        }, 200),
        updateOpeningPeriodFields(fields, selectedFields, tabulation) {
                //Si complément d'ouverture sélectionné, périodes d'ouvertures doit-être sélectionné
                    let compHoursField = fields.find(field => field.code === 'COMPLEMENT_OUVERTURE')
                    let openingHoursField = fields.find(field => field.code === 'OPENING_HOURS')
                    let openingHoursOld = selectedFields.find(field => field.code === 'OPENING_HOURS')
                    let compHoursOld = selectedFields.find(field => field.code === 'COMPLEMENT_OUVERTURE')

                    if (compHoursField && openingHoursField === undefined && compHoursOld && openingHoursOld) {
                        let index = fields.findIndex(field => field.code === 'COMPLEMENT_OUVERTURE')

                        if (index > -1) {
                            fields.splice(index, 1)
                        }
                    } else if (compHoursOld === undefined && openingHoursOld === undefined
                      && compHoursField && openingHoursField === undefined) {
                        let openingHoursChild = tabulation.children.find(field => field.code === 'OPENING_HOURS')

                        fields.push(openingHoursChild)
                    }

                    return fields
        },
        updateSelectedFields(index, fields, selectedFields = [], byPassLock = false) {
            if (!this.lockedCheck || byPassLock) {
                let tabulation = this.tabulations.find(tab => tab.id === index)

                if (tabulation && tabulation.code === 'OPENING_HOURS') {
                    //Si complément d'ouverture sélectionné, périodes d'ouvertures doit-être sélectionné
                    fields = this.updateOpeningPeriodFields(fields, selectedFields, tabulation)
                }

                this.setSelectedFields([index, fields])

                this.checkedTabulations[index] = fields.length > 0

                if (tabulation) {
                    tabulation.children.filter(field =>
                      this.selectedFields[index].find(selectedField => selectedField.id === field.id) === undefined &&
                      field.required === true && field.name !== "Portée").forEach(field => {
                        field.required = false
                    })
                }

                this.$forceUpdate()
            }
        },
        selectAllFields(tabulations) {
            tabulations.forEach(tabulation => {
                let fields = this.deepReduce(tabulation.children)
                fields = fields.filter(field => field.selected)

                this.updateSelectedFields(tabulation.id, fields)
            })
        },
        deepReduce(array) {
            return array.reduce((acc, val) => {
                let endValue = this.hasChildren(val) ? this.deepReduce(val.children, acc) : val

                return acc.concat(endValue)
            }, [])
        },
        hasChildren(node) {
            return (typeof node === "object")
              && (typeof node.children !== "undefined")
              && (node.children.length > 0)
        },
        hasFieldsInTabulation(tabulation) {
            return this.selectedFields[tabulation.id].length !== 0
        },
        checkTabulation(event, tabulation) {
            if (event) {
                this.tabulations.forEach(tab => {
                    if (tab.id === tabulation.id) {
                        this.updateSelectedFields(tabulation.id, tabulation.children)
                    }
                })
            } else {
                this.updateSelectedFields(tabulation.id, [])
            }

            this.$forceUpdate()
        },
        isTabulationEnabled(tabulation) {
            let enabled = false

            this.availableCategories.forEach(category => {
                if (category !== undefined && category.name === tabulation.code) {
                    enabled = true
                }
            })

            return enabled
        },
        showPreview() {
            let fields = this.getFieldsArray()

            this.setCategoryFields([this.selectedCategory.name, fields])
            this.setPreviewEditedTabulations(this.editedTabulations)
            this.setPreviewTxtIntro(this.txtIntro)

            let routeData = this.$router.resolve({
                path: "/preview/",
                query: { category: this.selectedCategory.name, aspect: this.aspect },
            })

            window.open(routeData.href, "_blank")
        },
        getFieldsArray() {
            let fieldsArray = Object.values(this.selectedFields)
            fieldsArray = [].concat.apply([], fieldsArray)

            return fieldsArray
        },
        sortNonTypeDependantTabulations() {
            let sorted = []
            let types = ["GENERAL", "ACCES_HANDICAPES", "COMMUNICATION", "PLATFORM", "LOCALISATION", "OPENING_HOURS", "TARIF",
                "ROOM_RENTING", "MEDIA", "COVID", "CONTACTS", "EXTRA"]

            types.forEach(type => {
                let exist = this.nonTypeDepenant.find(tabulation => tabulation.code === type)

                if (exist !== null && exist !== undefined) {
                    sorted.push(exist)
                }
            })

            this.nonTypeDepenant = sorted
        },
        editField(field) {
            this.currentField = field
            this.newTitle = field.new_title
            this.helpDescription = field.help_description
            this.fieldModal = true
        },
        editTabulation(tabulation) {
            this.currentTabulation = tabulation
            this.newTabTitle = tabulation.new_title
            this.tabDesc = tabulation.help_description
            this.tabulationModal = true
        },
        requiredField(field, tabulation) {
            field.required = !field.required

            if (field.required) {
                field.selected = true

                if (this.selectedFields[tabulation.id].find(selectedField => field.id === selectedField.id) === undefined) {
                    let fields = JSON.parse(JSON.stringify(this.selectedFields[tabulation.id]))
                    fields.push(JSON.parse(JSON.stringify(field)))

                    // section critique. Sans locker, la fonction updateSelectedFields est appelée par la @input du
                    // treeview et écrase la donnée. Résultat : le champs n'est pas coché si on le rend obligatoire
                    // et qu'il n'est pas coché de base
                    this.lockedCheck = true
                    this.updateSelectedFields(tabulation.id, fields, [], true)

                    setTimeout(() => {
                        this.lockedCheck = false
                    }, 1000)
                }
            }

            this.$forceUpdate()
        },
        saveField() {
            this.currentField.new_title = this.newTitle
            this.currentField.help_description = this.helpDescription

            this.newTitle = null
            this.helpDescription = null

            this.fieldModal = false
        },
        saveTabulation() {
            this.currentTabulation.new_title = this.newTabTitle
            this.currentTabulation.help_description = this.tabDesc

            this.newTabTitle = null
            this.tabDesc = null

            let tabIsInEditedTabArray = this.editedTabulations.find(tab => {
                return tab.code === this.currentTabulation.code
            })

            if (this.checkIfTabulationIsEdited() && !tabIsInEditedTabArray
            ) {
                this.addEditedTabulation(this.currentTabulation)
            } else if (this.checkIfTabulationIsEdited()) {
                let index = this.editedTabulations.findIndex(tab => tab.code === this.currentTabulation.code)

                this.deleteEditedTabulation(index)
                this.addEditedTabulation(this.currentTabulation)
            } else if (tabIsInEditedTabArray) {
                let index = this.editedTabulations.findIndex(tab => tab.code === this.currentTabulation.code)

                this.deleteEditedTabulation(index)
            }

            this.tabulationModal = null
        },
        checkIfTabulationIsEdited() {
            return (this.currentTabulation.new_title !== undefined && this.currentTabulation.new_title !== null &&
              this.currentTabulation.new_title !== "") || (this.currentTabulation.help_description !== undefined &&
              this.currentTabulation.help_description !== null && this.currentTabulation.help_description !== "")
        },
        hasFieldEditionEnabled(field) {
            let code = field.code !== undefined ? field.code : null

            return code === null || !this.disabledEditField.includes(code)
        },
        hasFieldRequiredEnabled(field) {
            let code = field.code !== undefined ? field.code : null

            return code === null || this.enabledRequiredField.includes(code)
        },
        init() {
            this.typeDependant = this.tabulations.filter(tabulation => tabulation.is_type_dependant)
            this.nonTypeDepenant = this.tabulations.filter(tabulation => !tabulation.is_type_dependant)

            if (this.selectAll !== undefined) {
                this.selectAllFields(this.tabulations)
            }

            this.tabulations.forEach(tabulation => {
                let fields = this.deepReduce(tabulation.children)
                fields = fields.filter(field => field.selected)

                this.updateSelectedFields(tabulation.id, fields)
                this.$set(this.checkedTabulations, tabulation.id, this.hasFieldsInTabulation(tabulation))
            })

            if(this.account.tis_type.code === 'APIDAE') {
                this.setFemPorteerequired()
            }
            this.uncheckNotDisplayedTabs()
            this.sortNonTypeDependantTabulations()
        },
        uncheckNotDisplayedTabs() {
            this.typeDependant.forEach(tabulation => {
                if (!this.isTabulationEnabled(tabulation)) {
                    this.$set(this.checkedTabulations, tabulation.id, false)

                    this.updateSelectedFields(tabulation.id, [])
                }
            })
        },
        setFemPorteerequired() {
            let femTabulation = this.tabulations.find(tab => tab.code === "FETE_ET_MANIFESTATION")
            if (femTabulation) {
                let femPortee = femTabulation.children.find(child => child.code === "FeteEtManifestationPortee")
                femPortee.required = true
            }
        },
        getTemplates() {
            this.loading = true

            this.$http.get("auth/account/web-form-templates").then(res => {
                this.templates = res.body.data.templates
            })

            this.loading = false
        },
        loadTemplate(template) {
            this.setPreviewEditedTabulations([])
            this.resetEditedTabulations()

            let tabulations = template.tabulations

            this.typeDependant = tabulations.filter(tabulation => tabulation.is_type_dependant)
            this.nonTypeDepenant = tabulations.filter(tabulation => !tabulation.is_type_dependant)

            this.selectAllFields(tabulations)

            tabulations.forEach(tabulation => {
                this.$set(this.checkedTabulations, tabulation.id, this.hasFieldsInTabulation(tabulation))
                if (tabulation.help_description || tabulation.new_title) {
                    this.addEditedTabulation(tabulation)
                }
            })

            this.setPreviewEditedTabulations(this.editedTabulations)

            this.sortNonTypeDependantTabulations()

            this.setTxtIntro(template.web_form_configuration.text_intro === null ?
              '' : template.web_form_configuration.text_intro)
        },
        closeModal() {
            this.openModal = false
            this.newTitle = null
            this.helpDescription = null
        },
        removeGoodReasonDescription() {
            let generalTab = this.tabulations.find(tab => tab.code === "GENERAL")

            if (generalTab !== undefined) {
                generalTab.children.forEach((child, index) => {
                    if (child.code === "DESC_GOOD_REASONS") {
                        generalTab.children.splice(index, 1)
                    }
                })
            }
        },
        removeIndicationTarif() {
            let tarifTab = this.tabulations.find(tab => tab.code === "TARIF")

            if (tarifTab !== undefined) {
                tarifTab.children.forEach((child, index) => {
                    if (child.code === "IndicationTarif") {
                        tarifTab.children.splice(index, 1)
                    }
                })
            }
        },
        removeQuebecLabelForNonQuebecUser() {
            let categoriesWithQuebecLabels = ["RESTAURATION", "PRODUCTEUR", "PATRIMOINE_CULTUREL",
                "HOTELLERIE_PLEIN_AIR", "HOTELLERIE", "HEBERGEMENT_LOCATIF", "HEBERGEMENT_COLLECTIF", "EQUIPEMENT",
                "COMMERCE_ET_SERVICE", "ACTIVITE", "FETE_ET_MANIFESTATION"]

            this.tabulations.forEach(tab => {
                if (categoriesWithQuebecLabels.includes(tab.code)) {
                    tab.children.forEach((child, index) => {
                        if (child.code === "QuebecLabels") {
                            tab.children.splice(index, 1)
                        }
                    })
                }
            })
        },
        updateTxtIntro(text) {
            this.setTxtIntro(text)
        },
        isAspectField(field) {
            return !!(this.aspect && aspectFields.includes(field.code))
        },
        selectOnlyAspectFields() {
            this.tabulations.forEach(tab => {
                tab.children.forEach(field => {
                    if(field.selected) {
                        if(tab.code === 'ROOM_RENTING') {
                            field.selected = false
                            if(field.children) {
                                field.children.forEach(child => {
                                    child.selected = false
                                })
                            }
                        } else {
                            field.selected = this.isAspectField(field);
                        }
                    }
                })
            })
        },
        loadTextIntro() {
            if(this.selectedIntroType === 'Mise à jour Standard') {
                this.setTxtIntro(this.txtIntroCmaj)
            } else {
                this.setTxtIntro(this.txtIntroAspect)
            }

            this.loadIntroModal = false
        }
    },
    computed: {
        ...mapState("createCmaj", ["selectedFields", "currentStep", "emailsValid", "editedTabulations", "txtIntro",
            'txtIntroAspect', 'txtIntroCmaj']),
        ...mapState("resource", ["categories"]),
        ...mapState("account", ["account"]),
        availableCategories() {
            if (this.provider === undefined) {
                let categoryLabels = this.emailsValid.map(provider => provider.category)
                let uniques = [...new Set(categoryLabels)]

                if (this.emailsValid.length) {
                    uniques.map((label, index) => {
                        let category = this.categories.find(cat => cat.label === label)

                        if (category !== null) {
                            uniques[index] = category
                        }
                    })
                } else {
                    uniques = this.categories
                }

                const priceTab = this.tabulations.find(tab => tab.code === "TARIF")

                if (priceTab && priceTab.children.filter(child => child.code === "IndicationTarif").length === 0 &&
                  uniques.filter(category => category && category.name === "FETE_ET_MANIFESTATION").length > 0) {

                    this.tabulations.find(tab => tab.code === "TARIF").children.push(this.tarifIndication)
                }

                if (uniques.filter(category => category && category.name === "FETE_ET_MANIFESTATION").length === 0) {
                    this.removeIndicationTarif()
                }

                return uniques
            } else {
                if (this.provider?.last_version.category.name !== "FETE_ET_MANIFESTATION") {
                    this.removeIndicationTarif()
                }
                return [this.provider?.last_version.category]
            }
        },
        aspectElement() {
            if(this.aspect !== undefined && this.aspect !== '') {
                return aspectElements.find(aspect => {
                    return aspect.name === this.aspect
                })
            }
        }
    },
    beforeDestroy() {
        this.$global.$off("reload-info")
    },
    created() {
        let priceTab = this.tabulations.find(tab => tab.code === "TARIF")

        if (priceTab) {
            priceTab.children.forEach(child => {
                if (child.code === "IndicationTarif") {
                    this.tarifIndication = child
                }
            })
        }

        if(this.aspect && !this.updateMode) {
            this.selectOnlyAspectFields()
        }

        this.init()
        this.getTemplates()

        this.$global.$on("reload-info", () => {
            this.init()
        })

        // Le champs 3 bonnes raisons de nous choisir n'est dispo que sur Tourinsoft
        if (this.account.tis_type.code === "APIDAE") {
            this.removeGoodReasonDescription()
            if(this.account.configurations.aspectCampaign) {
                this.introTypes.push('Mise à jour Aspect')
            }
        }

        //cacher les champs labels spécifiques au Quebec
        if (this.account.tis_type.code !== "DAHUB_QUEBEC") {
            this.removeQuebecLabelForNonQuebecUser()
        }
    },
}
</script>